import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Candidate } from "@/types/database"; // Import Candidate type

interface UseAssessmentDataProps {
  token: string | null;
}

export function useAssessmentData({ token }: UseAssessmentDataProps) {
  // Fetch candidate data by access token
  const candidateQuery = useQuery({
    queryKey: ["candidate-by-token", token],
    queryFn: async () => {
      if (!token) {
        throw new Error("Missing assessment token");
      }

      const { data, error } = await (supabase as any)
        .rpc("get_assessment_candidate", { p_access_token: token })
        .maybeSingle();

      if (error) throw error;
      if (!data) return null;

      // Check if token has expired
      if (
        data.token_expires_at &&
        new Date(data.token_expires_at) < new Date()
      ) {
        throw new Error("Assessment token has expired");
      }

      // Explicitly cast data to Candidate type to ensure type compatibility
      return data as Candidate;
    },
    retry: false,
    enabled: !!token,
  });

  // Fetch college data based on candidate's college_id
  const collegeQuery = useQuery({
    queryKey: ["college-for-assessment", candidateQuery.data?.college_id],
    queryFn: async () => {
      if (!candidateQuery.data?.college_id) return null;

      const { data, error } = await supabase
        .from("colleges")
        .select("*")
        .eq("id", candidateQuery.data.college_id)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!candidateQuery.data?.college_id,
  });

  // Check existing evaluations to prevent retaking
  const evaluationsQuery = useQuery({
    queryKey: ["candidate-evaluations", candidateQuery.data?.id],
    queryFn: async () => {
      if (!candidateQuery.data?.id) return [];

      const { data, error } = await supabase
        .from("evaluation_rounds")
        .select("*")
        .eq("candidate_id", candidateQuery.data.id)
        .in("round_name", ["mcq", "open_ended"]);

      if (error) throw error;
      return data;
    },
    enabled: !!candidateQuery.data?.id,
  });

  // Fetch question groups filtered by job position
  const questionGroupsQuery = useQuery({
    queryKey: [
      "question-groups-assessment",
      candidateQuery.data?.job_position_id,
    ],
    queryFn: async () => {
      if (!candidateQuery.data?.job_position_id) {
        console.warn("No job position ID found for candidate");
        return [];
      }

      const { data, error } = await supabase
        .from("question_groups")
        .select("*")
        .contains("job_position_ids", [candidateQuery.data.job_position_id])
        .order("sort_order", { ascending: true });

      if (error) throw error;

     
      return data || [];
    },
    enabled: !!candidateQuery.data?.job_position_id,
  });

  // Fetch questions for current group with proper random selection
  const questionsQuery = useQuery({
    queryKey: [
      "assessment-questions",
      candidateQuery.data?.current_group_index,
      candidateQuery.data?.id, // Include candidate ID for consistent random selection
      questionGroupsQuery.data?.map((g) => g.id).join(","),
    ],
    queryFn: async () => {
      if (!questionGroupsQuery.data || !candidateQuery.data) return [];

      const currentGroupIndex = candidateQuery.data.current_group_index || 0;
      const currentGroup = questionGroupsQuery.data[currentGroupIndex];
      if (!currentGroup) return [];

      

      // Fetch all questions for this group
      const { data: allQuestions, error } = await supabase
        .from("questions")
        .select("*")
        .eq("group_id", currentGroup.id)
        .eq("is_enabled", true)
        .order("created_at", { ascending: true });

      if (error) throw error;

      

      // Validate configuration
      const questionsPerTest = currentGroup.questions_per_test;
      const mustHaveCount = currentGroup.must_have_questions;

      if (mustHaveCount > questionsPerTest) {
        console.error(
          `Invalid configuration: must_have_questions (${mustHaveCount}) > questions_per_test (${questionsPerTest})`
        );
        throw new Error(
          `Question group "${currentGroup.name}" has invalid configuration: must-have questions exceed total questions per test`
        );
      }

      // Separate must-have and optional questions
      const mustHaveQuestions = allQuestions.filter((q) => q.is_must_have);
      const optionalQuestions = allQuestions.filter((q) => !q.is_must_have);

      
      

      // Validate we have enough questions
      if (mustHaveQuestions.length < mustHaveCount) {
        console.error(
          `Not enough must-have questions: need ${mustHaveCount}, have ${mustHaveQuestions.length}`
        );
        throw new Error(
          `Question group "${currentGroup.name}" doesn't have enough must-have questions (need ${mustHaveCount}, have ${mustHaveQuestions.length})`
        );
      }

      const optionalNeeded = questionsPerTest - mustHaveCount;
      if (optionalQuestions.length < optionalNeeded) {
        console.error(
          `Not enough optional questions: need ${optionalNeeded}, have ${optionalQuestions.length}`
        );
        throw new Error(
          `Question group "${currentGroup.name}" doesn't have enough optional questions (need ${optionalNeeded}, have ${optionalQuestions.length})`
        );
      }

      // Create deterministic random selection based on candidate ID
      const candidateId = candidateQuery.data.id;
      const seed = candidateId + currentGroup.id; // Consistent seed for same candidate + group

      // Simple seeded random function
      const seededRandom = (str: string, index: number) => {
        let hash = 0;
        const combined = str + index.toString();
        for (let i = 0; i < combined.length; i++) {
          const char = combined.charCodeAt(i);
          hash = (hash << 5) - hash + char;
          hash = hash & hash; // Convert to 32-bit integer
        }
        return Math.abs(hash) / 2147483648; // Normalize to 0-1
      };

      // Randomly select must-have questions
      const shuffledMustHave = [...mustHaveQuestions]
        .map((q, i) => ({ question: q, random: seededRandom(seed, i) }))
        .sort((a, b) => a.random - b.random)
        .map((item) => item.question);
      const selectedMustHave = shuffledMustHave.slice(0, mustHaveCount);

      // Randomly select optional questions
      const shuffledOptional = [...optionalQuestions]
        .map((q, i) => ({ question: q, random: seededRandom(seed, i + 1000) }))
        .sort((a, b) => a.random - b.random)
        .map((item) => item.question);
      const selectedOptional = shuffledOptional.slice(0, optionalNeeded);

      // Combine and shuffle the final set
      const finalQuestions = [...selectedMustHave, ...selectedOptional]
        .map((q, i) => ({ question: q, random: seededRandom(seed, i + 2000) }))
        .sort((a, b) => a.random - b.random)
        .map((item) => item.question);

     

      return finalQuestions;
    },
    enabled: !!questionGroupsQuery.data && !!candidateQuery.data,
  });

  // Fetch existing responses
  const responsesQuery = useQuery({
    queryKey: ["candidate-responses", candidateQuery.data?.id],
    queryFn: async () => {
      if (!candidateQuery.data?.id) return [];

      const { data, error } = await supabase
        .from("candidate_responses")
        .select("*")
        .eq("candidate_id", candidateQuery.data.id);

      if (error) throw error;
      return data;
    },
    enabled: !!candidateQuery.data?.id,
  });

  // Fetch job position data based on candidate's job_position_id
  const jobPositionQuery = useQuery({
    queryKey: [
      "job-position-for-assessment",
      candidateQuery.data?.job_position_id,
    ],
    queryFn: async () => {
      if (!candidateQuery.data?.job_position_id) return null;

      const { data, error } = await supabase
        .from("job_positions")
        .select("*")
        .eq("id", candidateQuery.data.job_position_id)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!candidateQuery.data?.job_position_id,
  });

  return {
    candidate: candidateQuery.data,
    college: collegeQuery.data,
    jobPosition: jobPositionQuery.data,
    questionGroups: questionGroupsQuery.data,
    questions: questionsQuery.data,
    existingResponses: responsesQuery.data,
    existingEvaluations: evaluationsQuery.data,
    isLoading:
      candidateQuery.isLoading ||
      collegeQuery.isLoading ||
      jobPositionQuery.isLoading ||
      questionGroupsQuery.isLoading ||
      questionsQuery.isLoading,
    error:
      candidateQuery.error ||
      collegeQuery.error ||
      jobPositionQuery.error ||
      questionGroupsQuery.error ||
      questionsQuery.error,
  };
}
